import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FileText, Plus, Download, Play, ChevronRight,
  BarChart2, Shield, Users, CreditCard, BookOpen, Bell, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FioriFilterBar,
  FioriTable,
  FioriStatusBadge,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import ReportView from "./ReportView";
import ReportBuilder from "./ReportBuilder";

const DATA_SOURCE_LABELS: Record<string, string> = {
  requests: "Access & Requests",
  approvals: "Approvals",
  checkpoints: "Checkpoint Ops",
  security: "Security & Alerts",
  cards: "Card Management",
  knowledge: "Knowledge & Compliance",
  users: "Users",
  workflows: "Workflows",
};

const DATA_SOURCE_ICONS: Record<string, React.ElementType> = {
  requests: Users,
  approvals: Users,
  checkpoints: Shield,
  security: Bell,
  cards: CreditCard,
  knowledge: BookOpen,
  users: Users,
  workflows: BarChart2,
};

type Report = {
  id: number;
  name: string;
  description: string | null;
  dataSource: string;
  isSystem: boolean;
  definition: unknown;
  createdAt: Date;
};

interface ReportLibraryProps {
  canCreate?: boolean;
  canExport?: boolean;
  /** Extra rows prepended to the report list (e.g. the Ticketing report) */
  prependReports?: Report[];
  /** Handle opening a row; return true if handled (skips the built-in ReportView) */
  onOpenReport?: (id: number) => boolean;
}

export default function ReportLibrary({ canCreate = false, canExport = false, prependReports = [], onOpenReport }: ReportLibraryProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);

  const { data: reports, isLoading, refetch, isRefetching } = trpc.analytics.reports.list.useQuery(
    undefined,
    { refetchOnWindowFocus: false }
  );

  const exportMutation = trpc.analytics.exports.create.useMutation({
    onSuccess: () => toast.success("Export queued — you'll receive an email when ready."),
    onError: () => toast.error("Failed to queue export."),
  });

  const filtered = useMemo(() => {
    const all = reports ?? [];
    return all.filter((r) => {
      const matchSearch =
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        (r.description ?? "").toLowerCase().includes(search.toLowerCase());
      const matchSource = filterSource === "all" || r.dataSource === filterSource;
      return matchSearch && matchSource;
    });
  }, [reports, search, filterSource]);

  const dataSources = useMemo(
    () => Array.from(new Set((reports ?? []).map((r) => r.dataSource))),
    [reports]
  );

  const columns: FioriColumn<Report>[] = useMemo(
    () => [
      {
        key: "name",
        header: t('analytics.reportName'),
        render: (r) => {
          const Icon = DATA_SOURCE_ICONS[r.dataSource] ?? FileText;
          return (
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-[#5B2C93]/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#5B2C93]" />
              </div>
              <span className="font-medium text-[#5B2C93] text-sm">{r.name}</span>
            </div>
          );
        },
      },
      {
        key: "description",
        header: t('common.description'),
        render: (r) => (
          <span className="text-sm text-[#6B6B6B] line-clamp-1">
            {r.description ?? "—"}
          </span>
        ),
      },
      {
        key: "category",
        header: t('analytics.dataSource'),
        width: "160px",
        hideOnMobile: true,
        render: (r) => (
          <span className="text-xs text-[#6B6B6B]">
            {DATA_SOURCE_LABELS[r.dataSource] ?? r.dataSource}
          </span>
        ),
      },
      {
        key: "type",
        header: t('common.type'),
        width: "90px",
        render: (r) => (
          <FioriStatusBadge
            status={r.isSystem ? "info" : "success"}
            label={r.isSystem ? t('analytics.system') : t('analytics.custom')}
          />
        ),
      },
      {
        key: "chart",
        header: t('analytics.chartType'),
        width: "80px",
        hideOnMobile: true,
        render: (r) => {
          const def = r.definition as any;
          const chartType = def?.chartType ?? "table";
          return (
            <Badge variant="outline" className="text-xs capitalize">
              {chartType}
            </Badge>
          );
        },
      },
      {
        key: "createdBy",
        header: t('analytics.createdBy'),
        width: "100px",
        hideOnMobile: true,
        render: (r) => (
          <span className="text-xs text-[#6B6B6B]">
            {r.isSystem ? t('analytics.system') : t('common.custom')}
          </span>
        ),
      },
      {
        key: "actions",
        header: t('common.actions'),
        width: canExport ? "130px" : "80px",
        align: "right",
        render: (r) => (
          <div className="flex items-center justify-end gap-1">
            {/* Run is always visible to anyone with analytics:view */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-[#5B2C93] hover:bg-[#5B2C93]/10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedReportId(r.id);
              }}
            >
              <Play className="w-3 h-3 mr-1" />
              {t('analytics.runReport')}
            </Button>
            {/* Export only visible when user has analytics:export */}
            {canExport && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-[#6B6B6B] hover:text-[#5B2C93]"
                onClick={(e) => {
                  e.stopPropagation();
                  exportMutation.mutate({ reportId: r.id, format: "csv" });
                }}
              >
                <Download className="w-3 h-3 mr-1" />
                {t('analytics.exportReport')}
              </Button>
            )}
            <ChevronRight className="w-4 h-4 text-[#B0B0B0]" />
          </div>
        ),
      },
    ],
    [exportMutation, canExport]
  );

  if (selectedReportId !== null) {
    return (
      <ReportView
        reportId={selectedReportId}
        onBack={() => setSelectedReportId(null)}
      />
    );
  }

  // Only show Report Builder if user has analytics:create
  if (showBuilder && canCreate) {
    return (
      <ReportBuilder
        onBack={() => setShowBuilder(false)}
        onSaved={() => {
          setShowBuilder(false);
          refetch();
        }}
      />
    );
  }

  const activeFilters =
    filterSource !== "all"
      ? [
          {
            key: "source",
            label: `Source: ${DATA_SOURCE_LABELS[filterSource] ?? filterSource}`,
            onRemove: () => setFilterSource("all"),
          },
        ]
      : [];

  return (
    <div className="space-y-5 p-4 md:p-6">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('analytics.totalReports'), value: reports?.length ?? 0, color: "text-[#2C2C2C]" },
          { label: t('analytics.systemReports'), value: reports?.filter((r) => r.isSystem).length ?? 0, color: "text-[#5B2C93]" },
          { label: t('analytics.customReports'), value: reports?.filter((r) => !r.isSystem).length ?? 0, color: "text-green-600" },
          { label: t('analytics.dataSources'), value: dataSources.length, color: "text-[#6B6B6B]" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[#E0E0E0] rounded-lg p-3">
            <p className="text-xs text-[#6B6B6B]">{s.label}</p>
            <p className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <FioriFilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('analytics.searchReports')}
        activeFilters={activeFilters}
        onClearAll={() => {
          setSearch("");
          setFilterSource("all");
        }}
        filters={
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-[180px] h-9 text-sm border-[#E0E0E0]">
              <SelectValue placeholder={t('analytics.allSources')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('analytics.allSources')}</SelectItem>
              {Object.entries(DATA_SOURCE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        trailing={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
              className="gap-1.5"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`}
              />
              {t('common.refresh')}
            </Button>
            {/* New Report button only visible when user has analytics:create */}
            {canCreate && (
              <Button
                size="sm"
                onClick={() => setShowBuilder(true)}
                className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('analytics.newReport')}
              </Button>
            )}
          </div>
        }
      />

      {/* Reports table */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-[#2C2C2C]">{t('nav.reports')}</h2>
          <Badge variant="secondary" className="text-xs">
            {filtered.length}
          </Badge>
        </div>
        <FioriTable<Report>
          columns={columns}
          data={[...prependReports, ...filtered]}
          isLoading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => {
            if (onOpenReport?.(r.id)) return;
            setSelectedReportId(r.id);
          }}
          emptyIcon={<FileText className="w-8 h-8 text-[#B0B0B0]" />}
          emptyTitle={t('analytics.noReports')}
          emptyDescription={canCreate ? t('analytics.noReportsDesc') : t('common.noResults')}
          footerInfo={`${t('table.showing')} ${filtered.length} ${t('table.of')} ${reports?.length ?? 0}`}
        />
      </div>
    </div>
  );
}
